/**
 * The create forms' Name field (F-147). Three sources, latest wins:
 *  - the form's DEFAULT (empty for long-form, `Short · Oct 12` for a Short —
 *    it follows the shoot day for as long as nothing else has spoken);
 *  - the imported FILE: picking one names the board `2026Oct08_Keywords`
 *    (`boardNameFromFile`) — over whatever was typed before, because
 *    picking the file is the later, deliberate act;
 *  - what the user TYPES after that, always.
 * F-155: a Short filled from Local Files is named after the file as it is
 * — `Ep12 cold open.csv` → `Ep12 cold open`, no date, no keywords — with
 * `fileNameOnly`.
 * Removing the file goes back to the default unless the user has typed
 * since. The keywords come from the server; any failure there falls back to
 * the file name's own words, so the field is never left waiting.
 */
import {
  boardNameExcerpt,
  boardNameFromFile,
  fileStem,
  type AgentAttachment,
  type BoardNameSuggestionRequest,
  type BoardNameSuggestionResponse,
} from "@kola/shared";
import { useEffect, useState } from "react";

import { suggestBoardName } from "../../core/api.js";
import { todayDateKey } from "../../ui/date-key.js";

export type SuggestBoardName = (input: BoardNameSuggestionRequest) => Promise<BoardNameSuggestionResponse>;

/** The full name for a file, never rejecting: the server's keywords, else the file's own. */
export async function nameForFile(
  attachment: AgentAttachment,
  dateKey: string,
  suggest: SuggestBoardName = suggestBoardName,
): Promise<string> {
  let keywords: string | null = null;
  try {
    keywords = (await suggest({ fileName: attachment.name, excerpt: boardNameExcerpt(attachment.text) })).keywords;
  } catch {
    // Offline, unconfigured, rate-limited: the file name will do.
  }
  return boardNameFromFile(dateKey, attachment.name, keywords);
}

export function useBoardNameField(
  defaultName: string,
  attachment: AgentAttachment | null,
  options: { fileNameOnly?: boolean } = {},
) {
  const fileNameOnly = options.fileNameOnly === true;
  /** What the user typed since the last file pick; null = nothing. */
  const [typed, setTyped] = useState<string | null>(null);
  const [fromFile, setFromFile] = useState<{ attachment: AgentAttachment; name: string } | null>(null);

  useEffect(() => {
    if (attachment === null) return;
    setTyped(null);
    if (fileNameOnly) {
      setFromFile({ attachment, name: fileStem(attachment.name).trim() || attachment.name });
      return;
    }
    let cancelled = false;
    void nameForFile(attachment, todayDateKey()).then((name) => {
      if (!cancelled) setFromFile({ attachment, name });
    });
    return () => {
      cancelled = true;
    };
  }, [attachment, fileNameOnly]);

  const fileName = attachment !== null && fromFile?.attachment === attachment ? fromFile.name : null;
  const naming = attachment !== null && fileName === null && typed === null;
  const value = typed ?? (attachment !== null ? (fileName ?? "") : defaultName);
  return { value, setValue: setTyped, naming };
}
